"use client";

import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
} from "@mui/material";

const ClientInfo = ({
  setClientEmail,
  onValidationChange,
  isEditMode = false,
  initialData = null,
  onFormStateChange
}) => {
  const [formData, setFormData] = useState({
    clientName: "",
    organizationName: "",
    organizationPhoneNumber: "",
    contactPersonName: "",
    contactPersonPhone: "",
    contactPersonEmail: "",
    address: "",
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [errors, setErrors] = useState({});

  // Load initial data in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      const loadedData = {
        clientName: initialData.clientName || "",
        organizationName: initialData.organizationName || "",
        organizationPhoneNumber: initialData.organizationPhoneNumber || "",
        contactPersonName: initialData.contactPersonName || "",
        contactPersonPhone: initialData.contactPersonPhone || "",
        contactPersonEmail: initialData.contactPersonEmail || "",
        address: initialData.address || "",
      };
      setFormData(loadedData);
      setInitialFormData(loadedData);
      if (initialData.contactPersonEmail) {
        setClientEmail(initialData.contactPersonEmail);
      }
    }
  }, [isEditMode, initialData]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUSPhone = (phone) => {
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  };

  const formatUSPhone = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleChange = (field) => (e) => {
    let value = e.target.value;

    // Apply auto-formatting for phone number fields
    if (field === "contactPersonPhone" || field === "organizationPhoneNumber") {
      value = formatUSPhone(value);
    }

    setFormData({ ...formData, [field]: value });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleBlur = (field) => () => {
    const value = formData[field];
    let error = "";

    switch (field) {
      case "clientName":
        if (!value.trim()) {
          error = "Client Name is required";
        } else if (value.length > 100) {
          error = "Client Name must not exceed 100 characters";
        }
        break;
      case "contactPersonName":
        if (value && value.length > 100) {
          error = "Contact Person Name must not exceed 100 characters";
        }
        break;
      case "contactPersonPhone":
        if (value && !validateUSPhone(value)) {
          error = "Please enter a valid US phone number (e.g., 123-456-7890)";
        }
        break;
      case "contactPersonEmail":
        if (!value.trim()) {
          error = "Contact Person Email is required";
        } else if (!validateEmail(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "organizationName":
        if (value && value.length > 150) {
          error = "Organization Name must not exceed 150 characters";
        }
        break;
      case "organizationPhoneNumber":
        if (value && !validateUSPhone(value)) {
          error = "Please enter a valid US phone number (e.g., 123-456-7890)";
        }
        break;
      case "address":
        if (value && value.length > 250) {
          error = "Address must not exceed 250 characters";
        }
        break;
        break;
      default:
        break;
    }

    setErrors({ ...errors, [field]: error });
  };

  // Check if form is valid
  const isFormValid = () => {
    const hasRequiredFields =
      formData.clientName.trim() !== "" &&
      formData.contactPersonEmail.trim() !== "";

    const hasNoErrors = Object.values(errors).every(error => error === "");

    return hasRequiredFields && hasNoErrors;
  };

  // Check if form has changes (for edit mode)
  const hasChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  // Notify parent about validation status
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid(), formData);
    }
  }, [formData, errors]);

  // Notify parent about form state changes (for Save Changes button)
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange({
        isValid: isFormValid(),
        hasChanges: hasChanges(),
        formData: formData
      });
    }
  }, [formData, errors, initialFormData]);

  return (
    <Box component="form">
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
        className="rmui-card"
      >
        <Grid container spacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2, lg: 3 }}>
          {/* Left Column */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
            {/* Client Name */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Client Name <span style={{ color: "#FF4023" }}>*</span>
                </Typography>
                <TextField
                  label="Enter Client Name"
                  variant="filled"
                  required
                  value={formData.clientName}
                  onChange={handleChange("clientName")}
                  onBlur={handleBlur("clientName")}
                  error={!!errors.clientName}
                  helperText={errors.clientName}
                  inputProps={{ maxLength: 100 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Organization Phone Number */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Organization Phone Number
                </Typography>
                <TextField
                  label="Enter Phone Number (e.g., 123-456-7890)"
                  variant="filled"
                  value={formData.organizationPhoneNumber}
                  onChange={handleChange("organizationPhoneNumber")}
                  onBlur={handleBlur("organizationPhoneNumber")}
                  error={!!errors.organizationPhoneNumber}
                  helperText={errors.organizationPhoneNumber}
                  inputProps={{ maxLength: 14 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Contact Person Phone Number */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Contact Person Phone Number
                </Typography>
                <TextField
                  label="Enter Phone Number (e.g., 123-456-7890)"
                  variant="filled"
                  value={formData.contactPersonPhone}
                  onChange={handleChange("contactPersonPhone")}
                  onBlur={handleBlur("contactPersonPhone")}
                  error={!!errors.contactPersonPhone}
                  helperText={errors.contactPersonPhone}
                  inputProps={{ maxLength: 14 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Address */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Address
                </Typography>
                <TextField
                  label="Enter Address"
                  variant="filled"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={handleChange("address")}
                  onBlur={handleBlur("address")}
                  error={!!errors.address}
                  helperText={errors.address}
                  inputProps={{ maxLength: 250 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
            {/* Organization Name */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Organization Name
                </Typography>
                <TextField
                  label="Enter Organization Name"
                  variant="filled"
                  value={formData.organizationName}
                  onChange={handleChange("organizationName")}
                  onBlur={handleBlur("organizationName")}
                  error={!!errors.organizationName}
                  helperText={errors.organizationName}
                  inputProps={{ maxLength: 150 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Contact Person Name */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Contact Person Name
                </Typography>
                <TextField
                  label="Enter Contact Person Name"
                  variant="filled"
                  value={formData.contactPersonName}
                  onChange={handleChange("contactPersonName")}
                  onBlur={handleBlur("contactPersonName")}
                  error={!!errors.contactPersonName}
                  helperText={errors.contactPersonName}
                  inputProps={{ maxLength: 100 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Contact Person Email */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: "500", fontSize: "14px", mb: "10px" }}
                >
                  Contact Person Email <span style={{ color: "#FF4023" }}>*</span>
                </Typography>
                <TextField
                  label="Enter Contact Person Email"
                  variant="filled"
                  required
                  type="email"
                  value={formData.contactPersonEmail}
                  onChange={(e) => {
                    handleChange("contactPersonEmail")(e);
                    setClientEmail(e.target.value);
                  }}
                  onBlur={handleBlur("contactPersonEmail")}
                  error={!!errors.contactPersonEmail}
                  helperText={errors.contactPersonEmail}
                  inputProps={{ maxLength: 100 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: "1px solid #D5D9E2",
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: "#64748B",
                    fontSize: "12px",
                    fontStyle: "italic",
                  }}
                >
                  Note: Admin credentials will be sent to contact person email
                </Typography>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default ClientInfo;
